import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { HugeiconsIcon } from '@hugeicons/react';
import { AddSquareIcon } from '@hugeicons/core-free-icons';
import { useActiveOrganization } from '@/graphql/queries/organizations';
import {
  useGetOrgMembers,
  useGetOrgInvitations,
  useInviteMember,
  useUpdateMemberRole,
  useRemoveMember,
  useRevokeInvitation,
  useResendInvitation,
  useUpdateInvitationRoleAndResend,
} from '../hooks/use-org-users';
import { StatsCards } from '../components/stats-cards';
import { MembersTable } from '../components/members-table';
import { PendingInvitations } from '../components/pending-invitations';
import { InviteMemberDialog } from '../components/invite-member-dialog';
import { RemoveMemberDialog } from '../components/remove-member-dialog';
import { RevokeInvitationDialog } from '../components/revoke-invitation-dialog';
import { DuplicateInvitationDialog } from '../components/duplicate-invitation-dialog';
import { useOrgUsersUiStore } from '../store/use-org-users-ui-store';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui-kit/button';
import type {
  OrgMember,
  OrgInvitation,
  TeamStats,
  DuplicateInvitationInfo,
} from '../types/org-users.types';
import type { OrgRole } from '../../org-permissions/types/permissions.types';

const OrgUsersPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: org } = useActiveOrganization();

  const blocksOrgId = org?.BlocksOrgId;

  // Data fetching
  const { data: membersData, isLoading: membersLoading } = useGetOrgMembers(blocksOrgId);
  const { data: invitationsData, isLoading: invitationsLoading } =
    useGetOrgInvitations(blocksOrgId);

  const members = membersData?.members || [];
  const invitations = invitationsData?.invitations || [];

  // Mutations
  const inviteMutation = useInviteMember(blocksOrgId);
  const updateRoleMutation = useUpdateMemberRole(blocksOrgId);
  const removeMemberMutation = useRemoveMember(blocksOrgId);
  const revokeInvitationMutation = useRevokeInvitation(blocksOrgId);
  const resendInvitationMutation = useResendInvitation(blocksOrgId);
  const updateAndResendMutation = useUpdateInvitationRoleAndResend(blocksOrgId);

  // Dialog states — invite is in a Zustand store so the OrgShell mobile
  // topbar trigger and this page can both control it without prop drilling.
  const inviteOpen = useOrgUsersUiStore((s) => s.inviteDialogOpen);
  const setInviteOpen = useOrgUsersUiStore((s) => s.setInviteDialogOpen);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [removeMemberOpen, setRemoveMemberOpen] = useState(false);
  const [revokeInvitationOpen, setRevokeInvitationOpen] = useState(false);

  // Selected items for dialogs
  const [selectedMember, setSelectedMember] = useState<OrgMember | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<OrgInvitation | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInvitationInfo | null>(null);

  // Loading states for inline actions
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Calculate stats
  const stats: TeamStats = {
    admins: members.filter((m) => m.role === 'org_admin').length,
    editors: members.filter((m) => m.role === 'org_editor').length,
    members: members.filter((m) => m.role === 'org_member').length,
    pending: invitations.length,
  };

  // Refresh all data
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['orgMembers', blocksOrgId] }),
      queryClient.invalidateQueries({
        queryKey: ['orgInvitations', blocksOrgId],
      }),
    ]);
    setIsRefreshing(false);
    toast({ variant: 'success', title: t('ORGTEAM_TOASTS_DATAREFRESHED') });
  }, [blocksOrgId, queryClient, t, toast]);

  // Handle invite
  const handleInvite = async (
    email: string,
    firstName: string,
    lastName: string,
    role: OrgRole
  ) => {
    const result = await inviteMutation.mutateAsync({
      email,
      firstName,
      lastName,
      role,
    });

    if (!result.success) {
      if (result.code === 'ALREADY_MEMBER') {
        toast({
          variant: 'destructive',
          title: t('ORGTEAM_ERRORS_ALREADYMEMBER'),
        });
        throw new Error('Already a member');
      }

      if (result.code === 'DUPLICATE_INVITATION' && result.existingInvitation) {
        setDuplicateInfo({
          email,
          newRole: role,
          existingInvitation: result.existingInvitation,
        });
        setInviteOpen(false);
        setDuplicateDialogOpen(true);
        return;
      }
    }

    toast({
      variant: 'success',
      title: t('ORGTEAM_TOASTS_INVITATIONSENT', { email }),
    });
  };

  // Handle resend invitation (same role) from duplicate dialog
  const handleResendInvitation = async () => {
    if (!duplicateInfo) return;
    await resendInvitationMutation.mutateAsync(duplicateInfo.existingInvitation.id);
    toast({
      variant: 'success',
      title: t('ORGTEAM_TOASTS_INVITATIONRESENT', {
        email: duplicateInfo.email,
      }),
    });
    setDuplicateInfo(null);
  };

  // Handle update and resend invitation (new role) from duplicate dialog
  const handleUpdateAndResendInvitation = async () => {
    if (!duplicateInfo) return;
    await updateAndResendMutation.mutateAsync({
      invitationId: duplicateInfo.existingInvitation.id,
      role: duplicateInfo.newRole as OrgRole,
    });
    toast({
      variant: 'success',
      title: t('ORGTEAM_TOASTS_INVITATIONUPDATEDRESENT', {
        email: duplicateInfo.email,
      }),
    });
    setDuplicateInfo(null);
  };

  // Handle resend from pending invitations list
  const handleResendFromList = async (invitation: OrgInvitation) => {
    setResendingInvitationId(invitation.id);
    try {
      await resendInvitationMutation.mutateAsync(invitation.id);
      toast({
        variant: 'success',
        title: t('ORGTEAM_TOASTS_INVITATIONRESENT', {
          email: invitation.email,
        }),
      });
    } finally {
      setResendingInvitationId(null);
    }
  };

  // Handle role change
  const handleRoleChange = async (memberId: string, newRole: OrgRole) => {
    setUpdatingMemberId(memberId);
    try {
      await updateRoleMutation.mutateAsync({ userId: memberId, role: newRole });
      const member = members.find((m) => m.id === memberId);
      toast({
        variant: 'success',
        title: t('ORGTEAM_TOASTS_ROLEUPDATED', {
          name: member?.firstName || member?.email || '',
        }),
      });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  // Handle remove member
  const handleOpenRemoveMember = (member: OrgMember) => {
    setSelectedMember(member);
    setRemoveMemberOpen(true);
  };

  const handleConfirmRemoveMember = async () => {
    if (!selectedMember) return;
    await removeMemberMutation.mutateAsync(selectedMember.id);
    toast({
      variant: 'success',
      title: t('ORGTEAM_TOASTS_MEMBERREMOVED', {
        name: selectedMember.firstName || selectedMember.email,
      }),
    });
    setSelectedMember(null);
  };

  // Handle revoke invitation
  const handleOpenRevokeInvitation = (invitation: OrgInvitation) => {
    setSelectedInvitation(invitation);
    setRevokeInvitationOpen(true);
  };

  const handleConfirmRevokeInvitation = async () => {
    if (!selectedInvitation) return;
    await revokeInvitationMutation.mutateAsync(selectedInvitation.id);
    toast({
      variant: 'success',
      title: t('ORGTEAM_TOASTS_INVITATIONREVOKED'),
    });
    setSelectedInvitation(null);
  };

  return (
    <div className="space-y-8 font-nunito bg-white">
      {/* Header */}
      <div className="space-y-1.5 md:space-y-3  sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 justify-between ">
          <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--fg))] font-nunito">
            {t('ORGTEAM_HEADER_TITLE')}
          </h1>
          {/* Desktop only — on mobile, Refresh moves beside the search bar
              (passed into MembersTable below) and Invite lives in the
              OrgShell mobile topbar (driven by the Zustand store). */}
          <div className="hidden md:flex gap-2">
            <Button
              variant="primaryOutline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {t('ORGTEAM_HEADER_REFRESH')}
            </Button>
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              <HugeiconsIcon icon={AddSquareIcon} size={20} />
              {t('ORGTEAM_HEADER_INVITEMEMBER')}
            </Button>
          </div>
        </div>
        <p className="text-sm md:text-base">{t('ORGTEAM_HEADER_SUBTITLE')}</p>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={stats} isLoading={membersLoading} />

      {/* Members Table — mobile gets the Refresh button next to the search
          input via the `searchAction` slot; desktop still uses the page
          header refresh button above. */}
      <MembersTable
        members={members}
        isLoading={membersLoading}
        onRoleChange={handleRoleChange}
        onRemove={handleOpenRemoveMember}
        updatingMemberId={updatingMemberId}
        searchAction={
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="md:hidden h-10"
            aria-label={t('ORGTEAM_HEADER_REFRESH')}
          >
            <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        }
      />

      {/* Pending Invitations */}
      <PendingInvitations
        invitations={invitations}
        isLoading={invitationsLoading}
        onResend={handleResendFromList}
        onRevoke={handleOpenRevokeInvitation}
        resendingId={resendingInvitationId}
      />

      {/* Dialogs */}
      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onConfirm={handleInvite}
        maxUsers={org?.MaxUsers || 10}
        currentMemberCount={members.length}
      />

      <DuplicateInvitationDialog
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
        email={duplicateInfo?.email || ''}
        existingRole={duplicateInfo?.existingInvitation.role || null}
        newRole={duplicateInfo?.newRole || ''}
        onResend={handleResendInvitation}
        onUpdateAndResend={handleUpdateAndResendInvitation}
      />

      <RemoveMemberDialog
        open={removeMemberOpen}
        onOpenChange={setRemoveMemberOpen}
        member={selectedMember}
        onConfirm={handleConfirmRemoveMember}
      />

      <RevokeInvitationDialog
        open={revokeInvitationOpen}
        onOpenChange={setRevokeInvitationOpen}
        invitation={selectedInvitation}
        onConfirm={handleConfirmRevokeInvitation}
      />
    </div>
  );
};

export default OrgUsersPage;
